import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Link } from "@mui/material";
import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import { EventsList } from "@/shared/components/events";
import { FilesList } from "@/shared/components/files";
import { AlertDialog, SelectionButton } from "@/shared/components/index.tsx";
import { DatasetsTable } from "@/shared/components/SearchResultViews/index.tsx";
import {
  CollectionRecord,
  CollectionResourceRef,
} from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { EventRecord } from "@/shared/domain/events";
import { FileRecord } from "@/shared/domain/files";
import {
  type Filter,
  type SelectionOption,
} from "@/shared/foundation/primitives/Table";
import { Condition, Dataset } from "@/types";

import styles from "./CollectionItemsTab.module.css";

interface CollectionItemsTabProps {
  collection: CollectionRecord;
  loading: boolean;
}

export const CollectionItemsTab: React.FC<CollectionItemsTabProps> = ({
  collection,
  loading,
}) => {
  const { currentOrganization } = useAuth();
  const { goto } = useNavigation();

  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false);
  const [selectedDatasets, setSelectedDatasets] = React.useState<Set<string>>(
    new Set(),
  );

  const {
    mutate: updateCollection,
    mutateAsync: updateCollectionAsync,
    error,
  } = useUpdateCollection();

  const collectionConditions: Condition[] = React.useMemo(
    () => [
      {
        field: "collection.collection_id",
        comparator: "EQUALS" as const,
        value: collection.collection_id,
      },
    ],
    [collection.collection_id],
  );

  const collectionScope: Filter[] = React.useMemo(
    () => [
      {
        type: "string" as const,
        field: "collection.collection_id",
        comparator: "EQUALS" as const,
        values: [collection.collection_id],
      },
    ],
    [collection.collection_id],
  );

  const eventSelectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: EventRecord[] }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((e: EventRecord) => ({
                resource_type: "event" as const,
                resource_id: e.event_id,
              })),
            },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection],
  );

  const fileSelectionOptions: SelectionOption<FileRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((f) => ({
                resource_type: "file" as const,
                resource_id: f.file_id,
              })),
            },
          });
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((f) => f.file_id) },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection, goto],
  );

  const datasets = React.useMemo(
    () =>
      "dataset" in collection.resources
        ? (collection.resources["dataset"] as Dataset[])
        : [],
    [collection.resources],
  );

  const fileCount =
    "file" in collection.resources ? collection.resources["file"].length : 0;

  const eventCount =
    "event" in collection.resources ? collection.resources["event"].length : 0;

  const isEmpty = datasets.length + fileCount + eventCount === 0;

  if (isEmpty) {
    return (
      <Alert severity="info">
        <AlertTitle>Empty Collection</AlertTitle>
        {collection.resource_type === "dataset" && (
          <>
            There are no items in this collection yet. You can add{" "}
            <Link href="/datasets" underline="hover">
              datasets
            </Link>{" "}
            to it.
          </>
        )}
        {collection.resource_type === "file" && (
          <>
            There are no items in this collection yet. You can add individual
            files to it.
          </>
        )}
        {collection.resource_type === "event" && (
          <>
            There are no items in this collection yet. You can add individual
            events to it.
          </>
        )}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}

      {selectedDatasets.size > 0 && (
        <div className={styles.actionsBar}>
          <SelectionButton
            selectedItems={[]}
            handleDeselectAll={() => {}}
            selectedDatasets={selectedDatasets}
            setSelectedDatasets={setSelectedDatasets}
            currentOrganization={currentOrganization}
            handleRemoveFromCollection={() => setRemoveDialogOpen(true)}
          />
        </div>
      )}

      {datasets.length > 0 && (
        <DatasetsTable
          data={datasets}
          dataCount={datasets.length}
          loading={loading}
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
          variant="outlined"
        />
      )}

      {collection.resource_type === "file" && (
        <FilesList
          scope={collectionScope}
          selectionOptions={fileSelectionOptions}
        />
      )}

      {collection.resource_type === "event" && (
        <div className={styles.eventsTableWrapper}>
          <EventsList
            conditions={collectionConditions}
            selectionOptions={eventSelectionOptions}
          />
        </div>
      )}

      <AlertDialog
        dialogOpen={removeDialogOpen}
        handleClose={() => setRemoveDialogOpen(false)}
        dialogTitle="Confirm Removal"
        dialogText={`Are you sure you want to remove ${selectedDatasets.size} item${selectedDatasets.size !== 1 ? "s" : ""} from this collection?`}
        dialogAction={async () => {
          const removeResources: CollectionResourceRef[] = Array.from(
            selectedDatasets,
          ).map((id) => ({
            resource_type: "dataset" as const,
            resource_id: id,
          }));
          try {
            await updateCollectionAsync({
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: { remove_resources: removeResources },
            });
          } finally {
            setSelectedDatasets(new Set());
          }
        }}
      />
    </>
  );
};
